import React from 'react';
import { borrower, loanDetails, loanHeader } from '../../mock';
import { Home, MapPin, Calendar, Hash, Building2, Lock } from 'lucide-react';

function money(n) {
  const sign = n < 0 ? '-' : '';
  const abs = Math.abs(n);
  return `${sign}$${abs.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function BorrowerDetails() {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-12 gap-5">
      <div className="xl:col-span-7 rounded-2xl border hairline bg-white p-6 card-lift">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-xl bg-neutral-900 text-white flex items-center justify-center display text-lg font-bold border hairline">
              TR
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Primary Borrower</div>
              <div className="display text-[22px] font-bold text-neutral-900 tracking-tight">{borrower.fullName}</div>
              <div className="text-[11.5px] text-neutral-500 mt-0.5">{borrower.email} · {borrower.phone}</div>
            </div>
          </div>
          <div className="hidden md:flex flex-col items-end">
            <div className="text-[10.5px] uppercase tracking-wider text-neutral-500 font-bold">Loan Purpose</div>
            <div className="mt-0.5 text-[11.5px] font-bold text-neutral-900 bg-neutral-100 rounded-md px-2 py-1 border hairline">{loanDetails.loanPurpose}</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
          <InfoCell icon={Calendar} label="Date of Birth" value={borrower.dob} />
          <InfoCell icon={Hash} label="SSN" value={borrower.ssn} mono />
          <InfoCell icon={Building2} label="Property Type" value="SFR · 1 Unit" />
          <InfoCell icon={Home} label="Occupancy" value={borrower.propertyUse} />
        </div>

        <div className="mt-5 rounded-xl border hairline bg-neutral-50/60 p-4 flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-white border hairline flex items-center justify-center">
            <MapPin className="h-4 w-4 text-neutral-600" />
          </div>
          <div className="min-w-0">
            <div className="text-[10.5px] uppercase tracking-wider text-neutral-500 font-bold">Subject property</div>
            <div className="text-[12.5px] font-bold text-neutral-900 truncate">{borrower.propertyAddress}</div>
          </div>
          <div className="ml-auto hidden md:block text-[10.5px] text-neutral-500 mono">Kennewick, Benton County</div>
        </div>
      </div>

      <div className="xl:col-span-5 rounded-2xl border hairline bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-neutral-500 font-bold">Loan Snapshot</div>
            <div className="display text-[22px] font-bold text-neutral-900 tracking-tight">Non‑QM Refinance</div>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-md bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border border-[hsl(var(--kf-gold))]/30 text-[10.5px] font-bold px-2 py-1">
            <Lock className="h-3 w-3" /> LOCKED · {loanDetails.lockExpiration}
          </span>
        </div>

        <div className="mt-5 grid grid-cols-2 gap-3">
          <Stat label="Loan Amount" value={money(loanDetails.loanAmount)} accent />
          <Stat label="Rate" value={`${loanDetails.lockedRate.toFixed(3)}%`} />
          <Stat label="APR" value={`${loanDetails.apr.toFixed(3)}%`} />
          <Stat label="Term" value={`${loanDetails.loanTerm / 12} yr fixed`} />
        </div>

        <div className="mt-4 rounded-xl border hairline p-4">
          <div className="flex items-center justify-between">
            <span className="text-[11.5px] font-bold text-neutral-700">Monthly payment</span>
            <span className="display text-[22px] font-bold text-neutral-900 mono">{money(loanDetails.totalMonthlyPayment)}</span>
          </div>
          <div className="mt-3 space-y-1.5 text-[12px]">
            <PaymentRow label="Principal & Interest" value={loanDetails.principalInterest} total={loanDetails.totalMonthlyPayment} color="hsl(0 0% 8%)" />
            <PaymentRow label="Taxes" value={loanDetails.taxes} total={loanDetails.totalMonthlyPayment} color="hsl(var(--kf-gold))" />
            <PaymentRow label="Hazard Insurance" value={loanDetails.hazardInsurance} total={loanDetails.totalMonthlyPayment} color="hsl(0 0% 55%)" />
          </div>
        </div>

        <div className="mt-4 grid grid-cols-3 gap-3">
          <Ring label="LTV" value={loanDetails.ltv} suffix="%" />
          <Ring label="CLTV" value={loanHeader.cltv} suffix="%" />
          <Ring label="DTI" value={loanDetails.dti} suffix="%" accent="gold" />
        </div>
      </div>
    </div>
  );
}

function InfoCell({ icon: Icon, label, value, mono }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider text-neutral-500 font-bold">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`text-[13px] font-bold text-neutral-900 mt-1 ${mono ? 'mono' : ''}`}>{value}</div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-3.5 ${accent ? 'border-[hsl(var(--kf-gold))]/40 bg-[hsl(var(--kf-gold-soft))]/40' : 'hairline bg-white'}`}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-bold">{label}</div>
      <div className="mt-1 display text-[18px] font-bold tracking-tight text-neutral-900">{value}</div>
    </div>
  );
}

function PaymentRow({ label, value, total, color }) {
  const pct = (value / total) * 100;
  return (
    <div>
      <div className="flex items-center justify-between text-neutral-600">
        <span>{label}</span>
        <span className="mono text-neutral-900 font-bold">${value.toFixed(2)}</span>
      </div>
      <div className="h-1 rounded-full bg-neutral-100 overflow-hidden mt-1">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function Ring({ label, value, suffix = '', accent = 'ink' }) {
  const color = accent === 'gold' ? 'hsl(var(--kf-gold))' : 'hsl(0 0% 8%)';
  return (
    <div className="rounded-xl border hairline p-3 flex items-center gap-3">
      <div
        className="relative h-11 w-11 rounded-full flex items-center justify-center"
        style={{ background: `conic-gradient(${color} ${value}%, hsl(0 0% 92%) 0)` }}
      >
        <div className="h-8 w-8 rounded-full bg-white flex items-center justify-center mono text-[10px] font-bold text-neutral-900">
          {value}{suffix}
        </div>
      </div>
      <div>
        <div className="text-[10.5px] uppercase tracking-wider text-neutral-500 font-bold">{label}</div>
        <div className="text-[11px] text-neutral-600">within policy</div>
      </div>
    </div>
  );
}
